import { can } from '../utils/memberPermissions';
import React, { useId, useRef, useState } from 'react';
import { contactFieldError, enquiryContactErrors, formatEnquiryPhone, validEnquiryPhone } from '../utils/enquiryValidation';
import EnquiryInterest from '../components/EnquiryInterest';
import IndustryFields from '../components/IndustryFields';
import { EnquiryPeople } from '../components/RecordFields';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Phone, Mail,  Loader2, Wallet, Zap } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import FormError from '../components/FormError';
import { useAuth } from '../contexts/AuthContext';
import useEnquiryDraft from '../hooks/useEnquiryDraft';
import { leadsAPI } from '../services/api';

const SOURCE_OPTIONS = [
  { value: '', label: 'Select source (optional)' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const FormSection = ({ title, description, children, plain = false }) => (
  <section className={plain ? "space-y-3" : "bg-white rounded-xl border border-gray-200 overflow-hidden"}>
    <div className={plain ? "" : "px-4 py-3 bg-gray-50 border-b border-gray-200"}>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <div className={plain ? "" : "p-4"}>
      {children}
    </div>
  </section>
);

const FormField = ({ label, required, icon: Icon, children, helper, error }) => {
  const id = useId();
  const fields = React.Children.map(children, (child) =>
    React.isValidElement(child) && ['input', 'select', 'textarea'].includes(child.type)
      ? React.cloneElement(child, { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': [helper && !error && `${id}-help`, error && `${id}-error`].filter(Boolean).join(' ') || undefined, className: `${child.props.className || ''}${error ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}` }) : child
  );
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {Icon ? (
        <div className="relative">
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          {fields}
        </div>
      ) : (
        fields
      )}
      {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-700">{error}</p>}
      {helper && !error && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1.5">{helper}</p>}
    </div>
  );
};

const NewLead = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const primaryContactId = searchParams.get('primaryContactId') || null;
  const organisationId = searchParams.get('organisationId') || null;
  const scope = user?.id ? JSON.stringify([user.id, user.workspaceOwnerId || user.id, primaryContactId, organisationId]) : null;
  if (!can(user, 'createEnquiries')) return <p role="status" className="rounded-xl border bg-white p-4 text-sm text-gray-600">Your workspace owner has not enabled creating enquiries for your account.</p>;
  return <NewEnquiryForm key={scope || 'anonymous'} draftKey={scope ? `leadmarka-enquiry-draft-v1:${scope}` : null} primaryContactId={primaryContactId} organisationId={organisationId} />;
};

const NewEnquiryForm = ({ draftKey, primaryContactId, organisationId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialForm = {
    primaryContactId,
    organisationId,
    additionalContacts: [],
    title: '',
    customFields: {},
    name: '',
    phoneNumber: '',
    socialHandle: '',
    email: '',
    productOrService: '',
    catalogueItemId: null,
    variantSpecs: '',
    budgetRange: '',
    urgency: '',
    source: '',
    otherSource: null,
    referrerName: '',
    status: 'new',
  };
  const { draft, status: draftStatus, update: updateDraft, clear: clearDraft } = useEnquiryDraft(draftKey, {
    formData: initialForm, contactMode: primaryContactId ? 'existing' : 'new', contactMethod: 'phoneNumber',
  });
  const { formData, contactMode, contactMethod } = draft;
  const setFormData = formData => updateDraft({ formData });
  const setContactMode = contactMode => updateDraft({ contactMode });
  const setContactMethod = contactMethod => { updateDraft({ contactMethod }); setFieldErrors({}); setTouched({}); setError(''); };
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors,setFieldErrors]=useState({}),[touched,setTouched]=useState({});
  const formRef=useRef(null);
  const focusField=key=>{
    const input=formRef.current?.querySelector(`[name="${key}"]`);
    const details=input?.closest('details');if(details)details.open=true;
    input?.focus();
  };
  const changeContact=(key,event)=>{
    const input=event.target,raw=input.value,cursor=input.selectionStart;
    const next=key==='phoneNumber'?formatEnquiryPhone(raw):raw;
    setFormData({...formData,[key]:next});
    const impossiblePhone=key==='phoneNumber' && (!/^\+?[\d\s().-]*$/.test(next) || (next.startsWith('0') && next.replace(/\D/g,'').length>10) || next.replace(/\D/g,'').length>15);
    if(touched[key] || fieldErrors[key] || impossiblePhone)setFieldErrors(previous=>({...previous,[key]:contactFieldError(key,next,key==='name' || key===contactMethod)}));
    if(key==='phoneNumber' && cursor!==null && cursor<raw.length && next!==raw){
      const count=(raw.slice(0,cursor).match(/[+\d]/g)||[]).length;
      let position=0,seen=0;while(position<next.length && seen<count){if(/[+\d]/.test(next[position]))seen++;position++;}
      requestAnimationFrame(()=>{if(document.activeElement===input)input.setSelectionRange(position,position);});
    }
  };
  const blurContact=key=>{
    setTouched(previous=>({...previous,[key]:true}));
    setFieldErrors(previous=>({...previous,[key]:contactFieldError(key,formData[key],key==='name' || key===contactMethod)}));
  };

  const createLeadMutation = useMutation({
    mutationFn: (payload) => leadsAPI.create(payload),
    onSuccess: async ({ data: lead }) => {
      clearDraft();
      queryClient.setQueryData(['lead', lead.id], lead);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
      navigate(`/leads/${lead.id}`, { state: { leadCreated: true } });
    },
    onError: (err) => {
      const data=err?.response?.data;
      const serverField=data?.errors?.find(item=>['name','phoneNumber','email','socialHandle'].includes(item.path || item.param));
      const key=(['name','phoneNumber','email','socialHandle'].includes(data?.field)?data.field:null) || serverField?.path || serverField?.param || (/invalid phone number/i.test(data?.error || '')?'phoneNumber':null);
      if(key && !formData.primaryContactId){
        setFieldErrors(previous=>({...previous,[key]:contactFieldError(key,formData[key],true) || serverField?.msg || 'Check this contact detail and try again.'}));focusField(key);
      } else setError(data?.error || data?.errors?.[0]?.msg || 'Could not create enquiry');
    },
  });

  const loading = createLeadMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (contactMode === 'existing' && !formData.primaryContactId) {
      setError('Choose an existing contact before saving.');
      return;
    }
    const errors=formData.primaryContactId?{}:enquiryContactErrors(formData,contactMethod);
    setFieldErrors(errors);
    if(Object.keys(errors).length){focusField(Object.keys(errors)[0]);return;}
    if(!e.currentTarget.reportValidity())return;
    const payload = { ...formData, name: formData.name.trim() };
    for (const key of ['phoneNumber', 'email', 'socialHandle']) {
      payload[key] = formData[key].trim() || undefined;
    }
    if (!payload.primaryContactId && payload.phoneNumber) payload.phoneNumber=validEnquiryPhone(payload.phoneNumber).number;
    if (!payload.title?.trim()) delete payload.title;
    if (payload.primaryContactId) { delete payload.name; delete payload.phoneNumber; delete payload.email; delete payload.socialHandle; }
    payload.otherSource = payload.source === 'other' ? formData.otherSource?.trim() || null : null;
    if (payload.source !== 'referral') payload.referrerName = undefined;
    if (!payload.email?.trim()) payload.email = undefined;
    if (!payload.productOrService?.trim()) payload.productOrService = undefined;
    if (!payload.variantSpecs?.trim()) payload.variantSpecs = undefined;
    if (!payload.budgetRange?.trim()) payload.budgetRange = undefined;
    if (!payload.urgency?.trim()) payload.urgency = undefined;
    if (!payload.source?.trim()) payload.source = undefined;
    if (!payload.referrerName?.trim()) payload.referrerName = undefined;
    createLeadMutation.mutate(payload);
  };

  const inputClasses = "w-full border border-gray-300 rounded-lg text-base focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all";
  const inputWithIconClasses = "pl-10 pr-3 py-2.5 " + inputClasses;
  const inputWithoutIconClasses = "px-3 py-2.5 " + inputClasses;
  const selectClasses = "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white transition-all";
  const selectWithIconClasses = "pl-10 " + selectClasses;

  return (
    <div className="max-w-2xl mx-auto">
      <form ref={formRef} noValidate onSubmit={handleSubmit} onInvalidCapture={(event) => {
        const details = event.target.closest('details');
        if (details) details.open = true;
      }} className="space-y-4">
        <fieldset disabled={loading} className="space-y-4">

        <FormError message={error} />

        {/* Contact Information */}
        <FormSection title="Who is enquiring?">
          <EnquiryPeople value={formData} onChange={setFormData} allowNew compact showTitle={false} contactMode={contactMode} onContactModeChange={setContactMode}>
          {contactMode === 'new' && !formData.primaryContactId && <div className="mt-4">
          <div className="space-y-4">
            <FormField label="Name" required icon={User} error={fieldErrors.name}>
              <input
                type="text"
                name="name"
                autoComplete="name"
                maxLength={200}
                onBlur={()=>blurContact('name')}
                value={formData.name}
                onChange={event=>changeContact('name',event)}
                className={inputWithIconClasses}
                placeholder="e.g., John Smith"
                required
              />
            </FormField>

            <div className="grid gap-3 min-[480px]:grid-cols-[11rem_minmax(0,1fr)]">
            <FormField label="Contact method">
              <select value={contactMethod} onChange={(e) => setContactMethod(e.target.value)} className={selectClasses}>
                <option value="phoneNumber">Phone / WhatsApp</option>
                <option value="email">Email</option>
                <option value="socialHandle">Social handle</option>
              </select>
            </FormField>
            <FormField
              label={contactMethod === 'phoneNumber' ? 'Phone Number' : contactMethod === 'email' ? 'Email' : 'Social handle'}
              required
              error={fieldErrors[contactMethod]}
              icon={contactMethod === 'phoneNumber' ? Phone : contactMethod === 'email' ? Mail : undefined}
              helper={contactMethod === 'phoneNumber' ? 'Local numbers work too. Use +country code for other countries.' : contactMethod === 'socialHandle' ? 'Include the platform, e.g., Instagram: @jane or a Facebook profile link.' : undefined}
            >
              <input
                key={contactMethod}
                name={contactMethod}
                inputMode={contactMethod==='phoneNumber'?'tel':contactMethod==='email'?'email':'text'}
                onBlur={()=>blurContact(contactMethod)}
                autoComplete={contactMethod === 'phoneNumber' ? 'tel' : contactMethod === 'email' ? 'email' : 'off'}
                type={contactMethod === 'phoneNumber' ? 'tel' : contactMethod === 'email' ? 'email' : 'text'}
                value={formData[contactMethod]}
                onChange={event=>changeContact(contactMethod,event)}
                className={contactMethod === 'socialHandle' ? inputWithoutIconClasses : inputWithIconClasses}
                placeholder={contactMethod === 'phoneNumber' ? '077 123 4567' : contactMethod === 'email' ? 'e.g., jane@example.com' : 'e.g., Instagram: @jane'}
                maxLength={contactMethod==='phoneNumber'?60:contactMethod==='email'?254:300}
                required
              />
            </FormField>
            </div>
            <details className="py-1" open={['phoneNumber', 'email', 'socialHandle'].some(key => key !== contactMethod && formData[key].trim()) || undefined}>
              <summary className="min-h-[36px] cursor-pointer text-sm font-medium text-primary-700">More contact details (optional)</summary>
              <p className="mb-3 text-xs text-gray-500">Every detail you enter is saved with this person.</p>
              <div className="space-y-3">
                {['phoneNumber', 'email', 'socialHandle'].filter(key => key !== contactMethod).map(key => (
                  <FormField key={key} error={fieldErrors[key]} label={key === 'phoneNumber' ? 'Phone Number' : key === 'email' ? 'Email' : 'Social handle'}
                    helper={key === 'phoneNumber' ? 'Local numbers work too. Use +country code for other countries.' : key === 'socialHandle' ? 'Include the platform, e.g., Instagram: @jane.' : undefined}>
                    <input name={key} onBlur={()=>blurContact(key)} inputMode={key==='phoneNumber'?'tel':key==='email'?'email':'text'} type={key === 'phoneNumber' ? 'tel' : key === 'email' ? 'email' : 'text'}
                      value={formData[key]} onChange={event=>changeContact(key,event)}
                      className={inputWithoutIconClasses} maxLength={key==='phoneNumber'?60:key === 'email' ? 254 : 300} />
                  </FormField>
                ))}
              </div>
            </details>
          </div>
        </div>}
          </EnquiryPeople>
        </FormSection>
        <FormSection title="What are they interested in?">
          <div className="space-y-4">
            <EnquiryInterest value={formData} onChange={setFormData}/>
            <FormField label="Enquiry title (optional)"><input type="text" value={formData.title} onChange={event=>setFormData({...formData,title:event.target.value})} className={inputWithoutIconClasses} placeholder="e.g. iPhone 17 Pro Max upgrade" maxLength={200}/></FormField>
          </div>
        </FormSection>

        <details className="rounded-xl border border-gray-200 bg-white">
          <summary className="p-4 min-h-[48px] cursor-pointer font-medium text-primary-700">Additional details</summary>
          <div className="px-4 pb-4 space-y-5 divide-y divide-gray-200 [&>section+section]:pt-5">


            {/* Product Details */}
            <FormSection plain title="Enquiry details" description="Add what you know about their requirements">
              <div className="space-y-4">
                <FormField label="Requirements / notes">
                  <textarea
                    rows={3}
                    value={formData.variantSpecs}
                    onChange={(e) => setFormData({ ...formData, variantSpecs: e.target.value })}
                    className={inputWithoutIconClasses}
                    placeholder="e.g., preferred model, storage, location, or website features"
                    maxLength={300}
                  />
                </FormField>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Budget Range" icon={Wallet}>
                    <input
                      type="text"
                      value={formData.budgetRange}
                      onChange={(e) => setFormData({ ...formData, budgetRange: e.target.value })}
                      className={inputWithIconClasses}
                      placeholder="e.g., $500–$800"
                      maxLength={120}
                    />
                  </FormField>

                  <FormField label="Buying timeframe" icon={Zap}>
                    <select
                      value={formData.urgency}
                      onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                      className={selectWithIconClasses}
                    >
                      <option value="">Not set</option>
                      <option value="now">Ready now</option>
                      <option value="soon">Soon</option>
                      <option value="browsing">Just browsing</option>
                    </select>
                  </FormField>
                </div>
              </div>
            </FormSection>

            <FormSection plain title="Custom fields">
              <IndustryFields value={formData} onChange={setFormData} />
            </FormSection>

            {/* Lead Settings */}
            <FormSection plain title="How they found you">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Source">
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className={selectClasses}
                    >
                      {SOURCE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </FormField>

                </div>

                {formData.source === 'other' && <FormField label="Specify source"><input type="text" value={formData.otherSource || ''} onChange={event=>setFormData({...formData,otherSource:event.target.value})} className={inputWithoutIconClasses} placeholder="e.g. Google search, an event, a flyer" maxLength={120}/></FormField>}
                {formData.source === 'referral' && (
                  <FormField label="Referrer Name">
                    <input
                      type="text"
                      value={formData.referrerName}
                      onChange={(e) => setFormData({ ...formData, referrerName: e.target.value })}
                      className={inputWithoutIconClasses}
                      placeholder="e.g., Jane Doe"
                      maxLength={120}
                    />
                  </FormField>
                )}

              </div>
            </FormSection>
          </div>
        </details>

        {draftStatus !== 'empty' && <div className="text-sm">
          {draftStatus === 'unavailable' && <p role="alert" className="text-amber-900">Draft saving is unavailable. Keep this page open to avoid losing your changes.</p>}
          {draftStatus === 'invalid' && <p role="alert" className="text-amber-900">The saved draft could not be restored. Start a new enquiry below.</p>}
          {['saved', 'restored', 'invalid'].includes(draftStatus) && !confirmDiscard && <button type="button" disabled={loading} className="mt-2 text-primary-700 underline disabled:opacity-50" onClick={() => setConfirmDiscard(true)}>Discard draft</button>}
          {confirmDiscard && <div role="group" aria-label="Discard draft confirmation" className="mt-3 space-y-2">
            <p>Discard this draft and start again?</p>
            <div className="flex flex-wrap gap-3">
              <button type="button" disabled={loading} className="rounded-lg border px-3 py-2" onClick={() => setConfirmDiscard(false)}>Keep editing</button>
              <button type="button" disabled={loading} className="rounded-lg bg-danger-500 px-3 py-2 text-white" onClick={() => { clearDraft(); setConfirmDiscard(false); setError(''); }}>Discard and start over</button>
            </div>
          </div>}
        </div>}

        <div className="rounded-xl bg-white p-3 border border-gray-200 shadow-sm">

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white p-3 rounded-lg text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create enquiry'
            )}
          </button>
        </div>
        </fieldset>
      </form>
    </div>
  );
};

export default NewLead;
